import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Slider, ClickAwayListener } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

const TEAL = '#12AFB8';

/**
 * Range control for API key counts; styled to match RangeSliderDropdown / filter bar inputs.
 */
const ApiKeysRangeFilter = ({
  min = 0,
  max = 5,
  step = 1,
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  const [lo, hi] = Array.isArray(value) ? value : [min, max];

  const marks = [];
  for (let v = min; v <= max; v += step) {
    marks.push({ value: v, label: String(v) });
  }

  const rangeLabel = `${lo}-${hi}`;

  return (
    <Box sx={{ position: 'relative', display: 'block', width: '100%' }}>
      <Box
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#FFFFFF',
          border: '1px solid #0000001A',
          borderRadius: open ? '1px 1px 0 0' : 1,
          boxShadow: '0px 2px 1px -1px #0000001A',
          padding: '8px',
          cursor: 'pointer',
          '&:hover': {
            borderColor: '#0000001A',
          },
        }}
      >
        <div className="row items-center">
          Range:
          <div className="root-text">{rangeLabel}</div>
        </div>

        <KeyboardArrowDown sx={{ color: 'var(--secondary-700)' }} />
      </Box>

      {open && (
        <ClickAwayListener onClickAway={handleClose}>
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 9999,
              borderRadius: 0,
              marginTop: 0,
              background: '#fff',
              border: '1px solid #0000001A',
              boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
              minWidth: '100%',
              p: 2,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" className="text-[#0000004D]" sx={{ display: 'block' }}>
                  Minimum
                </Typography>
                <Typography variant="subtitle2" className="text-headings font-bold">
                  {lo}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" className="text-[#0000004D]" sx={{ display: 'block' }}>
                  Maximum
                </Typography>
                <Typography variant="subtitle2" className="text-headings font-bold">
                  {hi}
                </Typography>
              </Box>
            </Box>

            <Slider
              value={[lo, hi]}
              onChange={(e, newValue) => {
                if (Array.isArray(newValue)) {
                  onChange(newValue);
                }
              }}
              min={min}
              max={max}
              step={step}
              valueLabelDisplay="off"
              disableSwap
              sx={{
                color: TEAL,
                '& .MuiSlider-rail': {
                  backgroundColor: '#E0E0E0',
                  opacity: 1,
                  height: 4,
                },
                '& .MuiSlider-track': {
                  backgroundColor: TEAL,
                  border: 'none',
                  height: 4,
                },
                '& .MuiSlider-thumb': {
                  width: 18,
                  height: 18,
                  backgroundColor: '#fff',
                  border: `2px solid ${TEAL}`,
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 8px rgba(18, 175, 184, 0.16)',
                  },
                },
              }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 0.5,
                mt: 0.5,
              }}
            >
              {marks.map((m) => (
                <Typography
                  key={m.value}
                  variant="caption"
                  className="text-[#0000004D]"
                  sx={{ fontSize: 11 }}
                >
                  {m.label}
                </Typography>
              ))}
            </Box>

            <Typography variant="subtitle2" className="text-[#0000004D]">
              Adjust slider to select range
            </Typography>
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

ApiKeysRangeFilter.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  value: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default ApiKeysRangeFilter;
